import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  selectAseQuestionPreviousAnswer,
  selectAseTplQuestion,
  selectAseTplQuestionAnswers,
} from 'store/assessments/selectors';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doAseSetQuestionResult } from 'store/assessments/actions';
import { useEsc } from 'hooks/useEsc';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { PageHeader } from 'components/PageHeader';

export const QuickAnswerQuestionModal: React.FC<
  ModalScreenProps<ModalScreens.AseQuickAnswerQuestion>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { questionId, fieldInstanceId, instructions, actionsAfter } =
    route.params;

  const question = useSelector((state) =>
    selectAseTplQuestion(state, questionId),
  );

  const answers = useSelector((state) =>
    selectAseTplQuestionAnswers(state, questionId),
  );

  const [answerId, setAnswerId] = React.useState<string>();

  const previousAnswerId = useSelector((state) =>
    selectAseQuestionPreviousAnswer(state, questionId, fieldInstanceId),
  );

  const handleSave = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    if (answerId) {
      dispatch(
        doAseSetQuestionResult({
          questionId,
          fieldInstanceId,
          answerId,
          comment: '',
          actionsAfter,
        }),
      );
    }
  };

  const isDirty = !!answerId;
  const handleClose = () => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Question"
          onPressCancelButton={handleClose}
          onPressSaveButton={answerId ? handleSave : null}
        />
      }
    >
      <Card bodyStyle={{ borderColor: theme.colors.yellow, borderWidth: 1 }}>
        <CardRow icon={ICONS.CircleInfoLight} value={instructions} />
      </Card>

      <CardGeneric>
        <Txt weight="bold" size="large">
          {question.question}
        </Txt>

        {!!question.description && (
          <Txt style={{ color: theme.colors.fieldValueText }}>
            {question.description}
          </Txt>
        )}
      </CardGeneric>

      {!!answers.length && (
        <Card>
          {answers.map((tplAnswer) => {
            return (
              <CardRow
                key={tplAnswer.id}
                style={{ backgroundColor: 'white' }}
                onPress={() => setAnswerId(tplAnswer.id)}
                radioValue={tplAnswer.id === answerId}
              >
                <Txt>
                  <Txt
                    style={tplAnswer.negative && { color: theme.colors.red }}
                  >
                    {tplAnswer.answer}
                  </Txt>
                  {tplAnswer.description && (
                    <Txt style={{ color: theme.colors.fieldValueText }}>
                      &nbsp;&nbsp;{tplAnswer.description}
                    </Txt>
                  )}
                </Txt>
                {previousAnswerId === tplAnswer.id && (
                  <Txt
                    size="small"
                    style={{
                      color: theme.colors.blue,
                      marginTop: theme.grid / 2,
                    }}
                  >
                    Most Recent Answer
                  </Txt>
                )}
              </CardRow>
            );
          })}
        </Card>
      )}
    </Page>
  );
};
